import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  Search,
  Edit,
  RefreshCw,
  AlertCircle,
  Key,
  Copy,
  Check,
  Eye,
  EyeOff,
  Building2,
  Shield,
  Filter,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { formatDate } from '../../lib/utils';
import { api } from '../../lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  type: 'ADMIN' | 'OPERATOR' | 'COMPLIANCE' | 'EMPLOYEE' | 'CUSTOMER' | 'SUPPLIER';
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  isSuperAdmin: boolean;
  createdAt: string;
  tenant?: {
    id: string;
    name: string;
  };
}

interface Tenant {
  id: string;
  name: string;
  cnpj: string;
}

const userTypeLabels = {
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  COMPLIANCE: 'Compliance',
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
};

const userTypeColors = {
  ADMIN: 'primary',
  OPERATOR: 'info',
  COMPLIANCE: 'warning',
  EMPLOYEE: 'info',
  CUSTOMER: 'success',
  SUPPLIER: 'warning',
} as const;

const statusLabels = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

export function SuperAdminUsers() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Password reset modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordTouched, setPasswordTouched] = useState(false);
  
  // Password validation
  const isPasswordValid = newPassword.length >= 8;
  const showPasswordError = passwordTouched && !isPasswordValid && newPassword.length > 0;
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    type: '' as User['type'],
    status: '' as User['status'],
  });

  // Fetch all tenants for filter dropdown
  const { data: tenantsData } = useQuery({
    queryKey: ['super-admin-tenants-list'],
    queryFn: async () => {
      const response = await api.get('/super-admin/tenants');
      return response.data as Tenant[];
    },
  });

  // Fetch users with filters
  const { data: usersData, isLoading, error, refetch } = useQuery({
    queryKey: ['super-admin-users', tenantFilter, typeFilter, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tenantFilter) params.append('tenantId', tenantFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (searchTerm) params.append('search', searchTerm);
      const response = await api.get(`/super-admin/users?${params.toString()}`);
      return response.data as User[];
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ tenantId, userId }: { tenantId: string; userId: string }) => {
      // First, update the password via the reset endpoint
      const response = await api.post(`/super-admin/tenants/${tenantId}/users/${userId}/reset-password`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-users'] });
      setGeneratedPassword(data.tempPassword);
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao resetar senha');
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ tenantId, userId, data }: { tenantId: string; userId: string; data: typeof editFormData }) => {
      const response = await api.put(`/super-admin/tenants/${tenantId}/users/${userId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-users'] });
      setIsEditModalOpen(false);
      setEditingUser(null);
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao atualizar usuário');
    },
  });

  // Toggle super admin mutation
  const toggleSuperAdminMutation = useMutation({
    mutationFn: async ({ userId, isSuperAdmin }: { userId: string; isSuperAdmin: boolean }) => {
      const response = await api.put(`/super-admin/users/${userId}/super-admin`, { isSuperAdmin });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-users'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao alterar privilégios');
    },
  });

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    const pwd = generatePassword();
    setNewPassword(pwd);
    setGeneratedPassword(null);
    setPasswordCopied(false);
    setShowPassword(true);
    setPasswordTouched(false);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordReset = () => {
    if (selectedUser && selectedUser.tenant) {
      resetPasswordMutation.mutate({
        tenantId: selectedUser.tenant.id,
        userId: selectedUser.id,
      });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      phone: user.phone || '',
      type: user.type,
      status: user.status,
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    if (editingUser && editingUser.tenant) {
      updateUserMutation.mutate({
        tenantId: editingUser.tenant.id,
        userId: editingUser.id,
        data: editFormData,
      });
    }
  };

  const handleToggleSuperAdmin = (user: User) => {
    const action = user.isSuperAdmin ? 'remover privilégios de' : 'conceder privilégios de Super Admin para';
    if (confirm(`Deseja ${action} ${user.name}?`)) {
      toggleSuperAdminMutation.mutate({
        userId: user.id,
        isSuperAdmin: !user.isSuperAdmin,
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const shareViaWhatsApp = () => {
    if (!selectedUser || !generatedPassword) return;
    const platformUrl = window.location.origin;
    const message = encodeURIComponent(
      `Olá! Suas credenciais de acesso à plataforma UniãoBR:\n\n` +
      `📧 Email: ${selectedUser.email}\n` +
      `🔑 Senha temporária: ${generatedPassword}\n` +
      `🌐 Acesse: ${platformUrl}\n\n` +
      `⚠️ Por segurança, altere sua senha no primeiro acesso.`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const users = usersData || [];
  const tenants = tenantsData || [];

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar usuários</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-primary-400" />
            Usuários Globais
          </h1>
          <p className="text-surface-400">Gerencie usuários de todos os tenants</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por nome, email ou CPF/CNPJ..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-64">
              <Select
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                options={[
                  { value: '', label: 'Todos os Tenants' },
                  ...tenants.map(t => ({ value: t.id, label: t.name })),
                ]}
              />
            </div>
            <div className="w-48">
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                options={[
                  { value: '', label: 'Todos os Tipos' },
                  { value: 'ADMIN', label: 'Administrador' },
                  { value: 'OPERATOR', label: 'Operador' },
                  { value: 'COMPLIANCE', label: 'Compliance' },
                  { value: 'EMPLOYEE', label: 'Funcionário' },
                  { value: 'CUSTOMER', label: 'Cliente' },
                  { value: 'SUPPLIER', label: 'Fornecedor' },
                ]}
              />
            </div>
            {(tenantFilter || typeFilter || searchTerm) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTenantFilter('');
                  setTypeFilter('');
                  setSearchTerm('');
                }}
              >
                <Filter className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-primary-400" />
            Lista de Usuários
            <Badge variant="info">{users.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <UsersIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum usuário encontrado</p>
              {(tenantFilter || typeFilter || searchTerm) && (
                <p className="text-sm mt-1">Tente ajustar os filtros</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {users.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-primary-400 font-semibold text-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-surface-100">{user.name}</p>
                        {user.isSuperAdmin && (
                          <Badge variant="error" className="text-xs">
                            <Shield className="w-3 h-3 mr-1" />
                            Super Admin
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-surface-500">{user.email}</p>
                      {user.tenant && (
                        <p className="text-xs text-surface-600 flex items-center gap-1 mt-0.5">
                          <Building2 className="w-3 h-3" />
                          {user.tenant.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={userTypeColors[user.type]}>
                      {userTypeLabels[user.type]}
                    </Badge>
                    <Badge variant={user.status === 'ACTIVE' ? 'success' : user.status === 'BLOCKED' ? 'error' : 'warning'}>
                      {statusLabels[user.status]}
                    </Badge>
                    <span className="text-sm text-surface-500 hidden lg:block">
                      {formatDate(user.createdAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        title="Editar usuário"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetPassword(user)}
                        title="Redefinir senha"
                        disabled={!user.tenant}
                      >
                        <Key className="w-4 h-4 text-yellow-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleSuperAdmin(user)}
                        title={user.isSuperAdmin ? 'Remover Super Admin' : 'Tornar Super Admin'}
                      >
                        <Shield className={`w-4 h-4 ${user.isSuperAdmin ? 'text-red-400' : 'text-surface-500'}`} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
        title="Editar Usuário"
      >
        <div className="space-y-4">
          {editingUser?.tenant && (
            <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700">
              <p className="text-sm text-surface-400 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Tenant: <span className="text-surface-200 font-medium">{editingUser.tenant.name}</span>
              </p>
            </div>
          )}
          <Input
            label="Nome"
            value={editFormData.name}
            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
          />
          <Input
            label="Telefone"
            value={editFormData.phone}
            onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
          />
          <Select
            label="Tipo"
            value={editFormData.type}
            onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value as User['type'] })}
            options={[
              { value: 'ADMIN', label: '👑 Administrador' },
              { value: 'OPERATOR', label: '⚙️ Operador' },
              { value: 'COMPLIANCE', label: '🛡️ Compliance' },
              { value: 'EMPLOYEE', label: '👤 Funcionário' },
              { value: 'CUSTOMER', label: '🧑‍💼 Cliente' },
              { value: 'SUPPLIER', label: '📦 Fornecedor' },
            ]}
          />
          <Select
            label="Status"
            value={editFormData.status}
            onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as User['status'] })}
            options={[
              { value: 'ACTIVE', label: '✅ Ativo' },
              { value: 'INACTIVE', label: '⏸️ Inativo' },
              { value: 'BLOCKED', label: '🚫 Bloqueado' },
            ]}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleEditSubmit}
              isLoading={updateUserMutation.isPending}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setSelectedUser(null);
          setGeneratedPassword(null);
          setNewPassword('');
        }}
        title="Redefinir Senha"
      >
        <div className="space-y-4">
          {selectedUser && (
            <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
              <p className="text-surface-300">
                Redefinindo senha para: <span className="font-semibold text-surface-100">{selectedUser.name}</span>
              </p>
              <p className="text-sm text-surface-500">{selectedUser.email}</p>
              {selectedUser.tenant && (
                <p className="text-xs text-surface-600 flex items-center gap-1 mt-1">
                  <Building2 className="w-3 h-3" />
                  {selectedUser.tenant.name}
                </p>
              )}
            </div>
          )}

          {!generatedPassword ? (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-surface-300">Nova Senha</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onBlur={() => setPasswordTouched(true)}
                      placeholder="Digite a nova senha"
                      error={showPasswordError ? 'A senha deve ter no mínimo 8 caracteres' : undefined}
                      hint={!showPasswordError && newPassword.length === 0 ? 'Mínimo de 8 caracteres' : undefined}
                    />
                  </div>
                  <Button type="button" variant="secondary" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setNewPassword(generatePassword())}>
                    Gerar
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="secondary" onClick={() => setIsPasswordModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handlePasswordReset}
                  isLoading={resetPasswordMutation.isPending}
                  disabled={!newPassword || newPassword.length < 8 || !selectedUser?.tenant}
                >
                  Redefinir Senha
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-medium mb-2">✅ Senha redefinida com sucesso!</p>
                <p className="text-sm text-surface-400 mb-3">
                  Copie a nova senha e envie para o usuário:
                </p>
                <div className="flex items-center gap-2 p-3 bg-surface-900 rounded border border-surface-700">
                  <code className="flex-1 text-primary-400 font-mono">
                    {showPassword ? generatedPassword : '••••••••••••'}
                  </code>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(generatedPassword!)}>
                    {passwordCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={shareViaWhatsApp}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="flex-1"
                  onClick={() => {
                    setIsPasswordModalOpen(false);
                    setSelectedUser(null);
                    setGeneratedPassword(null);
                    setNewPassword('');
                  }}
                >
                  Entendi
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </motion.div>
  );
}
